/**
 * How old something is, said the same way everywhere.
 *
 * The app grew two vocabularies for one idea. SERP timestamps said "Just now",
 * "3h ago", "2w ago", "Mar 5"; the Trends freshness chip said "today", "3d",
 * "2mo". Both are defensible and having both is not: the same table can show a
 * SERP fetched "2h ago" beside a keyword pulled "today", and a reader has to
 * work out whether those are different scales or different clocks.
 *
 * So one helper, in the terser Trends form. Dependency-free — CRA's jest cannot
 * resolve react-router-dom v7, and this has to be testable.
 */

/** Whole days between `then` and `now`, or null when there is no timestamp. */
export function daysSince(
  value: string | null | undefined,
  now: Date = new Date(),
): number | null {
  if (!value) return null;
  const ms = new Date(value).getTime();
  if (Number.isNaN(ms)) return null;
  // Clock skew between the API host and the browser can put a just-written
  // timestamp slightly in the future. Clamp rather than render "-1 days".
  return Math.max(0, Math.floor((now.getTime() - ms) / 86_400_000));
}

/**
 * "today" / "3d" / "2w" / "5mo".
 *
 * NO HOURS. The old SERP format had "Just now" and "6h ago", which is more
 * precision than any decision on these screens needs — nobody re-crawls a SERP
 * because it is four hours old rather than one — and it was the main reason the
 * two vocabularies could not simply be merged. Anything inside a day is "today".
 */
export function relativeAge(
  value: string | null | undefined,
  now: Date = new Date(),
): string {
  const days = daysSince(value, now);
  if (days === null) return 'age unknown';
  if (days === 0) return 'today';
  if (days === 1) return '1d';
  if (days < 14) return `${days}d`;
  if (days < 60) return `${Math.round(days / 7)}w`;
  const months = Math.round(days / 30);
  return `${months}mo`;
}

/** The full timestamp, for a tooltip behind the short form. */
export function exactDate(value: string | null | undefined): string {
  if (!value) return 'No date recorded';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? 'No date recorded' : d.toLocaleString();
}


/**
 * "14:05" today, then "1d" / "2w" — for a table somebody is WATCHING.
 *
 * `relativeAge` deliberately has no hours: nobody re-crawls a SERP because it
 * is four hours old rather than one. A board being read while a dig runs is
 * the opposite case — everything that lands is "today", so the one word that
 * would tell two arrivals apart is the only one it does not say. Sorting by
 * arrival is how you see an insertion, and the clock is how you read it.
 */
export function arrivedAt(
  value: string | null | undefined,
  now: Date = new Date(),
): string {
  const days = daysSince(value, now);
  if (days === null) return '-';
  if (days > 0) return relativeAge(value, now);
  const d = new Date(value as string);
  return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}
